import * as React from "react";
import { Check, ChevronRight, Sparkles, Plus, ArrowRight, X, Camera, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils.js";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { SPACE_ACTIVITIES, spaceStates, DEFAULT_ACTIVITIES_BY_ROOM_TYPE, normalizeRoomKey } from "@homethyst/shared/schemas";

interface Space {
  id: string;
  name: string;
  type: string;
  roomType: string | null;
  parentId: string | null;
  purpose: string | null;
  activities: string[] | null;
}

interface RoutineSuggestion {
  title: string;
  description: string;
  frequency: string;
  estimatedMinutes: number;
  tasks: { id: string; label: string }[];
}

interface Props {
  spaces: Space[];
  onComplete: () => void;
  onSkip: () => void;
  /** Pre-select a specific room (skips step 1) */
  initialSpaceId?: string;
}

type Step = "pick-room" | "set-intent" | "add-items" | "routine" | "done";

const STATE_LABELS: Record<string, { label: string; emoji: string }> = {
  organized: { label: "Organized", emoji: "✨" },
  somewhat_cluttered: { label: "Somewhat cluttered", emoji: "📦" },
  cluttered: { label: "Cluttered", emoji: "🗃️" },
  overwhelmed: { label: "Overwhelmed", emoji: "😰" },
  empty: { label: "Empty", emoji: "🫙" },
  in_progress: { label: "In progress", emoji: "🔧" },
};

const ROOM_ICONS: Record<string, string> = {
  kitchen: "🍳", "living room": "🛋️", "dining room": "🍽️", bedroom: "🛏️", bathroom: "🚿",
  garage: "🚗", attic: "📦", basement: "🏚️", office: "💻", "laundry room": "🧺", pantry: "🥫", closet: "👔",
};

export function RoomSetupFlow({ spaces, onComplete, onSkip, initialSpaceId }: Props) {
  const toast = useToast();

  // Filter to room/container spaces that don't have intent yet
  const eligibleSpaces = spaces.filter(
    (s) => (s.type === "room" || s.type === "container") && !s.purpose && (!s.activities || s.activities.length === 0)
  );
  const allRoomSpaces = spaces.filter((s) => s.type === "room" || s.type === "container");

  const [step, setStep] = React.useState<Step>(initialSpaceId ? "set-intent" : "pick-room");
  const [selectedSpaceId, setSelectedSpaceId] = React.useState<string | null>(initialSpaceId ?? null);
  const [saving, setSaving] = React.useState(false);

  // Intent fields
  const [purpose, setPurpose] = React.useState("");
  const [activities, setActivities] = React.useState<string[]>([]);
  const [currentState, setCurrentState] = React.useState("");

  // Quick-add items
  const [itemNames, setItemNames] = React.useState<string[]>([""]);
  const [addingItems, setAddingItems] = React.useState(false);

  // Routine suggestions
  const [routineSuggestions, setRoutineSuggestions] = React.useState<RoutineSuggestion[]>([]);
  const [loadingRoutines, setLoadingRoutines] = React.useState(false);
  const [creatingRoutine, setCreatingRoutine] = React.useState<string | null>(null);

  const selectedSpace = allRoomSpaces.find((s) => s.id === selectedSpaceId);
  const roomKey = normalizeRoomKey(selectedSpace?.roomType, selectedSpace?.name);
  const availableActivities = SPACE_ACTIVITIES[roomKey] ?? SPACE_ACTIVITIES._default;
  const defaultActivityValues = DEFAULT_ACTIVITIES_BY_ROOM_TYPE[roomKey] ?? [];
  const hasDefaultSuggestion = defaultActivityValues.length > 0;

  // Pre-select the weak room-type default when the user lands on the intent step
  // for a space that has no activities of its own yet. The user can freely change
  // or clear them; nothing is persisted until they hit Continue.
  React.useEffect(() => {
    if (!selectedSpace) return;
    const own = selectedSpace.activities ?? [];
    setActivities(own.length > 0 ? own : (DEFAULT_ACTIVITIES_BY_ROOM_TYPE[roomKey] ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpaceId]);

  const getIcon = (space: Space) =>
    ROOM_ICONS[(space.roomType ?? space.name ?? "").toLowerCase().replace(/\s*\d+$/, "")] ?? "📍";

  const toggleActivity = (value: string) => {
    setActivities((prev) => prev.includes(value) ? prev.filter((a) => a !== value) : [...prev, value]);
  };

  // Step 2 → save intent → move to items
  const handleSaveIntent = async () => {
    if (!selectedSpaceId) return;
    setSaving(true);
    try {
      await apiFetch(`/api/spaces/${selectedSpaceId}`, {
        method: "PUT",
        body: JSON.stringify({
          purpose: purpose || null,
          activities: activities.length > 0 ? activities : null,
          currentState: currentState || null,
        }),
      });
      setStep("add-items");
    } catch (e: any) {
      toast.show(e.message ?? "Failed to save", "error");
    }
    setSaving(false);
  };

  // Step 3 → save items → move to routines
  const handleSaveItems = async () => {
    const validNames = itemNames.filter((n) => n.trim());
    if (validNames.length === 0) {
      setStep("routine");
      fetchRoutineSuggestions();
      return;
    }
    setAddingItems(true);
    try {
      await apiFetch("/api/items/bulk", {
        method: "POST",
        body: JSON.stringify({ items: validNames.map((name) => ({ name: name.trim(), spaceId: selectedSpaceId, tags: [] })) }),
      });
      toast.show(`${validNames.length} item${validNames.length > 1 ? "s" : ""} added!`);
      setStep("routine");
      fetchRoutineSuggestions();
    } catch (e: any) {
      toast.show(e.message ?? "Failed to add items", "error");
    }
    setAddingItems(false);
  };

  const fetchRoutineSuggestions = async () => {
    if (!selectedSpaceId) return;
    setLoadingRoutines(true);
    try {
      const { suggestions } = await apiFetch<{ suggestions: RoutineSuggestion[] }>(
        `/api/spaces/${selectedSpaceId}/routine-suggestions`
      );
      setRoutineSuggestions(suggestions);
    } catch {
      toast.show("Couldn't load routine suggestions — you can skip or try again later.", "error");
    }
    setLoadingRoutines(false);
  };

  const handleAcceptRoutine = async (suggestion: RoutineSuggestion) => {
    setCreatingRoutine(suggestion.title);
    try {
      const nextDue = new Date();
      const daysOffset = suggestion.frequency === "daily" ? 1
        : suggestion.frequency === "weekly" ? 7
        : suggestion.frequency === "quarterly" ? 90 : 30;
      nextDue.setDate(nextDue.getDate() + daysOffset);
      await apiFetch("/api/guided-flows", {
        method: "POST",
        body: JSON.stringify({
          title: suggestion.title,
          description: suggestion.description,
          frequency: suggestion.frequency,
          spaceId: selectedSpaceId,
          nextDueDate: nextDue.toISOString(),
          estimatedMinutes: suggestion.estimatedMinutes,
          tasks: suggestion.tasks.map((t) => ({ ...t, isCompleted: false })),
        }),
      });
      toast.show(`"${suggestion.title}" routine created!`);
      setRoutineSuggestions((prev) => prev.filter((s) => s.title !== suggestion.title));
    } catch (e: any) {
      toast.show(e.message ?? "Failed to create routine", "error");
    }
    setCreatingRoutine(null);
  };

  // --- STEP: pick-room ---
  if (step === "pick-room") {
    if (eligibleSpaces.length === 0) {
      // All rooms already have intent — skip to completion
      return (
        <div className="text-center py-8 space-y-4 rounded-lg bg-sage-wash">
          <div className="text-4xl">✨</div>
          <h2 className="text-lg font-semibold text-ink">All rooms are set up!</h2>
          <p className="text-sm text-body">Every room has an intent defined. Nice work.</p>
          <button onClick={onComplete} className="px-6 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">
            Done
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-ink mb-1">Which room matters most right now?</h2>
          <p className="text-sm text-faint">Pick one to set up — you can do the rest later.</p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {eligibleSpaces.map((space) => (
            <button
              key={space.id}
              onClick={() => { setSelectedSpaceId(space.id); setStep("set-intent"); }}
              className="flex items-center gap-3 p-4 rounded-lg border border-clay-border hover:border-primary/40 hover:bg-amethyst-soft/50 text-left transition-colors"
            >
              <span className="text-2xl">{getIcon(space)}</span>
              <div>
                <div className="text-sm font-medium text-body">{space.name}</div>
                <div className="text-[0.8125rem] text-faint uppercase">{space.type}</div>
              </div>
            </button>
          ))}
        </div>

        <button onClick={onSkip} className="w-full py-2 text-[0.8125rem] text-faint hover:text-body transition-colors">
          I'll do this later
        </button>
      </div>
    );
  }

  // --- STEP: set-intent ---
  if (step === "set-intent") {
    return (
      <div className="space-y-5">
        <div className="text-center">
          <span className="text-3xl">{selectedSpace ? getIcon(selectedSpace) : "📍"}</span>
          <h2 className="text-lg font-semibold text-ink mt-2 mb-1">
            What's your {selectedSpace?.name ?? "space"} for?
          </h2>
          <p className="text-sm text-faint">This helps us suggest routines tailored to how you use this room.</p>
        </div>

        {/* Purpose one-liner */}
        <div>
          <label className="text-[0.8125rem] font-medium text-body mb-1.5 block">In one sentence, what's this space for?</label>
          <input
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="e.g. Daily cooking and family meals"
            className="w-full px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none"
            maxLength={200}
          />
        </div>

        {/* Activities */}
        <div>
          <label className="text-[0.8125rem] font-medium text-body mb-2 block">
            What activities happen here?
            {hasDefaultSuggestion && (
              <span className="ml-1.5 font-normal text-faint">· we pre-selected a suggestion</span>
            )}
          </label>
          <div className="flex flex-wrap gap-2">
            {availableActivities.map((act) => (
              <button
                key={act.value}
                onClick={() => toggleActivity(act.value)}
                className={cn(
                  "px-3 py-1.5 rounded-pill text-[0.8125rem] font-medium transition-colors",
                  activities.includes(act.value)
                    ? "bg-amethyst-soft text-amethyst-ink border border-primary"
                    : "bg-clay-muted text-body border border-clay-border hover:border-primary/40"
                )}
              >
                {act.label}
              </button>
            ))}
          </div>
        </div>

        {/* Current state — simplified single row */}
        <div>
          <label className="text-[0.8125rem] font-medium text-body mb-2 block">How is it right now?</label>
          <div className="flex flex-wrap gap-1.5">
            {spaceStates.map((state) => (
              <button
                key={state}
                onClick={() => setCurrentState(currentState === state ? "" : state)}
                className={cn(
                  "px-3 py-1.5 rounded-pill text-[0.8125rem] font-medium transition-colors",
                  currentState === state
                    ? "bg-amethyst-soft text-amethyst-ink border border-primary"
                    : "bg-clay-muted text-body border border-clay-border hover:border-primary/40"
                )}
              >
                {STATE_LABELS[state].emoji} {STATE_LABELS[state].label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          {!initialSpaceId && (
            <button onClick={() => setStep("pick-room")} className="px-4 py-2.5 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium hover:bg-amethyst-soft/70 transition-colors">
              Back
            </button>
          )}
          <button
            onClick={handleSaveIntent}
            disabled={saving || (!purpose.trim() && activities.length === 0)}
            className="flex-1 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed flex items-center justify-center gap-1"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ChevronRight className="w-4 h-4" /></>}
          </button>
        </div>
      </div>
    );
  }

  // --- STEP: add-items ---
  if (step === "add-items") {
    return (
      <div className="space-y-5">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-ink mb-1">Add a few key items</h2>
          <p className="text-sm text-faint">
            What's in your {selectedSpace?.name ?? "space"}? Just the important ones — you can always add more later.
          </p>
        </div>

        <div className="space-y-2">
          {itemNames.map((name, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={name}
                onChange={(e) => { const n = [...itemNames]; n[i] = e.target.value; setItemNames(n); }}
                placeholder={i === 0 ? "e.g. Stand Mixer" : `Item ${i + 1}`}
                className="flex-1 px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && name.trim()) {
                    setItemNames([...itemNames, ""]);
                  }
                }}
              />
              {itemNames.length > 1 && (
                <button onClick={() => setItemNames(itemNames.filter((_, j) => j !== i))} aria-label={`Remove item ${i + 1}`} className="p-2 text-faint hover:text-error transition-colors">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <button
            onClick={() => setItemNames([...itemNames, ""])}
            className="flex items-center gap-1 text-[0.8125rem] text-primary font-medium hover:text-amethyst-ink transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />Add another
          </button>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={() => { setStep("routine"); fetchRoutineSuggestions(); }}
            className="px-4 py-2.5 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium hover:bg-amethyst-soft/70 transition-colors"
          >
            Skip
          </button>
          <button
            onClick={handleSaveItems}
            disabled={addingItems}
            className="flex-1 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed flex items-center justify-center gap-1"
          >
            {addingItems ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Save & continue <ChevronRight className="w-4 h-4" /></>}
          </button>
        </div>
      </div>
    );
  }

  // --- STEP: routine ---
  if (step === "routine") {
    return (
      <div className="space-y-5">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-ink mb-1">Set up a routine?</h2>
          <p className="text-sm text-faint">
            Based on your intent for {selectedSpace?.name ?? "this space"}, here are some suggestions.
          </p>
        </div>

        {loadingRoutines ? (
          <div className="flex flex-col items-center py-6 gap-2">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
            <p className="text-[0.8125rem] text-faint">Finding routines that fit...</p>
          </div>
        ) : routineSuggestions.length === 0 ? (
          <div className="text-center py-6 rounded-lg bg-sage-wash">
            <Sparkles className="w-8 h-8 text-sage mx-auto mb-2" />
            <p className="text-sm text-body">No suggestions yet — you can create routines later from the Workflows page.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {routineSuggestions.map((suggestion) => (
              <div key={suggestion.title} className="p-4 rounded-lg border border-clay-border bg-card space-y-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-ink">{suggestion.title}</h4>
                    <p className="text-[0.8125rem] text-faint mt-0.5">{suggestion.description}</p>
                  </div>
                  <button
                    onClick={() => handleAcceptRoutine(suggestion)}
                    disabled={creatingRoutine === suggestion.title}
                    className="ml-3 px-3 py-1.5 rounded-md bg-primary text-white text-[0.8125rem] font-medium shrink-0 hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
                  >
                    {creatingRoutine === suggestion.title ? "..." : "Start"}
                  </button>
                </div>
                <div className="flex items-center gap-3 text-[0.8125rem] text-faint">
                  <span className="capitalize">{suggestion.frequency}</span>
                  <span className="tabular-nums">~{suggestion.estimatedMinutes} min</span>
                  <span className="tabular-nums">{suggestion.tasks.length} steps</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={() => setStep("done")}
          className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors flex items-center justify-center gap-1"
        >
          {routineSuggestions.length > 0 ? "Done" : "Finish setup"} <ArrowRight className="w-4 h-4" />
        </button>
        {routineSuggestions.length > 0 && (
          <button onClick={() => setStep("done")} className="w-full py-2 text-[0.8125rem] text-faint hover:text-body transition-colors">
            Skip routines for now
          </button>
        )}
      </div>
    );
  }

  // --- STEP: done ---
  return (
    <div className="text-center py-6 space-y-4">
      <div className="w-16 h-16 rounded-pill bg-sage-wash flex items-center justify-center mx-auto">
        <Check className="w-8 h-8 text-sage-deep" />
      </div>
      <h2 className="text-lg font-semibold text-ink">
        {selectedSpace?.name ?? "Room"} is set up! 🎉
      </h2>
      <p className="text-sm text-faint">
        You can always add more items and adjust routines from the space detail page.
      </p>
      <div className="flex flex-col gap-2 pt-2">
        {eligibleSpaces.filter((s) => s.id !== selectedSpaceId).length > 0 && (
          <button
            onClick={() => {
              // Reset state for another room
              setPurpose(""); setActivities([]); setCurrentState("");
              setItemNames([""]); setRoutineSuggestions([]);
              setSelectedSpaceId(null); setStep("pick-room");
            }}
            className="w-full py-2.5 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium hover:bg-amethyst-soft/70 transition-colors"
          >
            Set up another room
          </button>
        )}
        <button onClick={onComplete} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">
          I'm done for now
        </button>
      </div>
    </div>
  );
}
